import { useEffect, useState, useMemo } from "react";
import { MapControl, useMap } from "@vis.gl/react-google-maps";
import { Task } from "@/api.types";
import { UndoRedoControl } from "../../services/maps/undo-redo-control";
import { useTasks, filterTasksByBounds, taskSearchQueries } from "@/api/tasks";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";

export interface TasksSelectorProps {
  onSelectionComplete: (
    bounds: google.maps.LatLngBounds,
    tasks: Task[]
  ) => void;
}

export function TasksSelector({ onSelectionComplete }: TasksSelectorProps) {
  const { showPickups, showDeliveries, tripFilter } = useShortcutsStore();
  const map = useMap();
  const [drawingManager, setDrawingManager] =
    useState<google.maps.drawing.DrawingManager | null>(null);

  // Make separate API calls based on the toggle state
  const { data: pickupsResponse } = useTasks(
    taskSearchQueries.activePickupTasks
  );

  const { data: deliveriesResponse } = useTasks(
    taskSearchQueries.activeDeliveryTasks
  );

  const { data: tripsResponse } = useTasks(taskSearchQueries.activeTasks);

  // Combine results from different queries
  const tasks = useMemo(() => {
    const allTasks: Task[] = [];

    if (showPickups && pickupsResponse?.data) {
      allTasks.push(...pickupsResponse.data);
    }

    if (showDeliveries && deliveriesResponse?.data) {
      allTasks.push(...deliveriesResponse.data);
    }

    // Check if trips should be shown based on tripFilter
    if (
      (tripFilter.showAll || tripFilter.visibleTripIds.length > 0) &&
      tripsResponse?.data
    ) {
      // For tasks with trips, filter them here client-side
      const tasksWithTrips = tripsResponse.data.filter((task) => {
        // If task has a tripId, check if it should be visible based on filter
        if (task.tripId) {
          if (tripFilter.showAll) {
            // Show unless explicitly hidden
            return !tripFilter.hiddenTripIds.includes(task.tripId);
          } else {
            // Only show if explicitly visible
            return tripFilter.visibleTripIds.includes(task.tripId);
          }
        }
        return false;
      });

      allTasks.push(...tasksWithTrips);
    }

    return allTasks;
  }, [
    pickupsResponse?.data,
    deliveriesResponse?.data,
    tripsResponse?.data,
    showPickups,
    showDeliveries,
    tripFilter,
  ]);

  useEffect(() => {
    if (!map) return;

    const manager = new google.maps.drawing.DrawingManager({
      drawingMode: google.maps.drawing.OverlayType.RECTANGLE,
      drawingControl: false, // We'll control this via a button instead
      rectangleOptions: {
        fillColor: "#0066ff",
        fillOpacity: 0.2,
        strokeWeight: 2,
        strokeColor: "#0066ff",
        editable: false,
        draggable: false,
      },
    });

    manager.setMap(map);
    setDrawingManager(manager);

    return () => {
      manager.setMap(null);
    };
  }, [map]);

  useEffect(() => {
    if (!drawingManager) return;

    let currentRectangle: google.maps.Rectangle | null = null;

    const rectangleCompleteListener = google.maps.event.addListener(
      drawingManager,
      "rectanglecomplete",
      (rectangle: google.maps.Rectangle) => {
        // Remove the previous rectangle if it exists
        if (currentRectangle) {
          currentRectangle.setMap(null);
        }

        currentRectangle = rectangle;

        // Get the bounds of the rectangle
        const bounds = rectangle.getBounds();
        if (bounds) {
          // Use the extracted utility function to filter tasks
          const selectedTasks = filterTasksByBounds(tasks, bounds, {
            showPickups,
            showDeliveries,
            showTrips:
              tripFilter.showAll || tripFilter.visibleTripIds.length > 0,
          });

          // Pass both bounds and selected tasks to the callback
          onSelectionComplete(bounds, selectedTasks);
        }

        // Remove the rectangle after a short delay
        setTimeout(() => {
          rectangle.setMap(null);
          currentRectangle = null;
        }, 500);

        // Switch back to hand tool after drawing
        drawingManager.setDrawingMode(null);
      }
    );

    return () => {
      if (currentRectangle) {
        currentRectangle.setMap(null);
      }
      google.maps.event.removeListener(rectangleCompleteListener);
    };
  }, [
    drawingManager,
    tasks,
    showPickups,
    showDeliveries,
    tripFilter,
    onSelectionComplete,
  ]);

  return (
    <MapControl position={google.maps.ControlPosition.BOTTOM_CENTER}>
      <UndoRedoControl drawingManager={drawingManager} />
    </MapControl>
  );
}
